import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Users, MailPlus, ScanFace } from "lucide-react";
import { AppShell } from "../components/layout/AppShell";
import { NeuTabs } from "../components/neu/NeuTabs";
import { listBranches, listUsers } from "../api/directory";
import { listInvitations } from "../api/onboarding";
import { EmployeesKpiRow } from "../features/admin/EmployeesKpiRow";
import { EmployeesTeamTab } from "../features/admin/EmployeesTeamTab";
import { EmployeesInvitesTab } from "../features/admin/EmployeesInvitesTab";
import { EmployeesFaceTab } from "../features/admin/EmployeesFaceTab";
import { useAuth } from "../context/AuthContext";
import { isAdmin } from "../utils/role";
import { useUrlTab } from "../hooks/useUrlTab";
import { useCachedResource } from "../hooks/useCachedResource";

const TABS = ["team", "invites", "face"];

export default function EmployeesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [tab, setTab] = useUrlTab("team", "tab");
  const active = TABS.includes(tab) ? tab : "team";

  const { data, reload: load } = useCachedResource(`employees:${canEdit}`, async () => {
    const [e, b] = await Promise.all([listUsers("", ""), listBranches()]);
    const inv = canEdit ? await listInvitations() : [];
    return { employees: e, branches: b, invites: inv };
  });
  const employees = data?.employees || [];
  const branches = data?.branches || [];
  const invites = data?.invites || [];

  const branchNames = useMemo(
    () => branches.reduce((acc, b) => ({ ...acc, [b.id]: b.name }), {}),
    [branches]
  );

  const tabDefs = useMemo(() => {
    const base = [{ key: "team", label: t("employees.tabs.team"), icon: Users }];
    if (canEdit) {
      base.push({ key: "invites", label: t("employees.tabs.invites"), icon: MailPlus });
      base.push({ key: "face", label: t("employees.tabs.face"), icon: ScanFace });
    }
    return base;
  }, [canEdit, t]);

  return (
    <AppShell title={t("employees.title")} subtitle={t("employees.subtitle")}>
      <div className="grid gap-6">
        {canEdit ? <EmployeesKpiRow employees={employees} invites={invites} /> : null}
        <NeuTabs bare fill tabs={tabDefs} value={active} onChange={setTab} testid="employees-tabs" />
        <div data-testid={`employees-panel-${active}`}>
          {active === "team" ? (
            <EmployeesTeamTab employees={employees} branchNames={branchNames} />
          ) : null}
          {active === "invites" && canEdit ? (
            <EmployeesInvitesTab invites={invites} branches={branches} reload={load} />
          ) : null}
          {active === "face" && canEdit ? (
            <EmployeesFaceTab employees={employees} onDone={load} />
          ) : null}
        </div>
      </div>
    </AppShell>
  );
}
